import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  of,
  tap,
  shareReplay,
  map,
  catchError,
  switchMap,
  merge,
  filter,
  distinctUntilChanged,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  PORTAL_COMPONENT_KEYS,
  PortalComponentKey,
  ResolveBatchRequest,
  ResolveBatchResponse,
  ResolvedPortalLayout,
  ResolveSingleParams,
  isPortalComponentKey,
  readLayoutDisplayId,
} from './ad-layout.models';
import { mergeProductsPageResolvedPayload } from './products-page-layout.util';
import { VisitorCountryService } from 'src/app/Theme/shared/service/visitor-country.service';

@Injectable({
  providedIn: 'root',
})
export class PortalLayoutResolveService {
  private readonly host = environment.apiUrlHost;
  private readonly cache = new Map<PortalComponentKey, ResolvedPortalLayout>();
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);
  private readonly sessionAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private loadInFlight: Observable<ResolveBatchResponse> | null = null;
  /** Country code used for the current cached resolve-batch result. */
  private resolvedCountryCode: string | null | undefined;

  /** Emits when session layout cache has been populated (or cleared). */
  readonly loaded$ = this.loadedSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly visitorCountry: VisitorCountryService,
  ) {}

  /** POST /AD/layouts/resolve-batch/ — portal session bootstrap. */
  resolveBatch(body: ResolveBatchRequest): Observable<ResolveBatchResponse> {
    let params = new HttpParams();
    if (body.country_code) {
      params = params.set('country_code', body.country_code);
    }
    return this.http
      .post<ResolveBatchResponse>(`${this.host}/AD/layouts/resolve-batch/`, body, { params })
      .pipe(tap((data) => this.applyBatchResponse(data)));
  }

  /** GET /AD/layouts/resolve/?component=... — optional lazy load / admin preview. */
  resolveOne(params: ResolveSingleParams): Observable<ResolvedPortalLayout> {
    let httpParams = new HttpParams().set('component', params.component);
    if (params.bp) {
      httpParams = httpParams.set('bp', params.bp);
    }
    if (params.country) {
      httpParams = httpParams.set('country_code', params.country);
    }
    if (params.layout_display_id) {
      httpParams = httpParams.set('layout_display_id', params.layout_display_id);
    }
    return this.http
      .get<ResolvedPortalLayout>(`${this.host}/AD/layouts/resolve/`, { params: httpParams })
      .pipe(
        tap((entry) => {
          if (!isPortalComponentKey(params.component)) {
            return;
          }
          const normalized = this.normalizeResolvedEntry(entry, params.component);
          const previous = this.cache.get(params.component);
          const requestedId = params.layout_display_id?.trim() || '';
          const previousId = readLayoutDisplayId(previous);
          const incomingId = readLayoutDisplayId(normalized);
          // A resolve without a display id must not replace the layout already chosen for this session.
          if (!requestedId && previousId && incomingId && previousId !== incomingId) {
            this.loadedSubject.next(true);
            return;
          }
          const payload =
            params.component === 'products' && previous?.payload
              ? mergeProductsPageResolvedPayload(previous.payload, normalized.payload)
              : normalized.payload;
          this.cache.set(params.component, {
            ...normalized,
            layout_display_id: requestedId || incomingId || previousId,
            payload,
          });
          this.loadedSubject.next(true);
        }),
      );
  }

  /**
   * Load all portal layouts once per session (deduped).
   * Call after login and on auth status when already authenticated.
   *
   * @param countryCode `undefined` = detect visitor country from IP (public /home and customer portal).
   *                      `null` = skip geo lookup and omit country_code.
   */
  loadForSession(
    components: PortalComponentKey[] = [...PORTAL_COMPONENT_KEYS],
    countryCode?: string | null,
  ): Observable<ResolveBatchResponse> {
    return this.resolveVisitorCountry(countryCode).pipe(
      switchMap((code) => {
        if (this.cache.size > 0 && this.resolvedCountryCode === code) {
          const missingComponent = components.some((key) => !this.cache.has(key));
          if (!missingComponent) {
            return of(this.snapshotBatch());
          }
          this.cache.clear();
          this.loadInFlight = null;
        }
        if (this.loadInFlight && this.resolvedCountryCode === code) {
          return this.loadInFlight;
        }
        if (this.resolvedCountryCode !== code) {
          this.cache.clear();
          this.loadInFlight = null;
        }
        this.resolvedCountryCode = code;
        this.loadInFlight = this.resolveBatch({
          components,
          country_code: code ?? undefined,
        }).pipe(
          shareReplay(1),
          catchError((err) => {
            console.warn('[PortalLayoutResolve] resolve-batch failed', err);
            this.loadInFlight = null;
            return of({});
          }),
        );
        return this.loadInFlight;
      }),
    );
  }

  /** Force refetch (e.g. after admin assignment change while still logged in). */
  reloadSession(
    components: PortalComponentKey[] = [...PORTAL_COMPONENT_KEYS],
  ): Observable<ResolveBatchResponse> {
    this.clearCache();
    return this.loadForSession(components, undefined);
  }

  clearCache(): void {
    this.cache.clear();
    this.loadInFlight = null;
    this.resolvedCountryCode = undefined;
    this.loadedSubject.next(false);
  }

  /**
   * Session auth state, pushed by `TfService` on login / auth status / logout.
   * Session-scoped watchers only hit resolve-batch once this is `true`.
   */
  setSessionAuthenticated(isAuthenticated: boolean): void {
    if (this.sessionAuthenticatedSubject.getValue() !== isAuthenticated) {
      this.sessionAuthenticatedSubject.next(isAuthenticated);
    }
  }

  isSessionAuthenticated(): boolean {
    return this.sessionAuthenticatedSubject.getValue();
  }

  /** Country from visitor IP detection (cached for the browser session). */
  getVisitorCountryCode(): Observable<string | null> {
    return this.visitorCountry.getCountryCode();
  }

  isLoaded(): boolean {
    return this.cache.size > 0;
  }

  getEntry(component: PortalComponentKey): ResolvedPortalLayout | null {
    return this.cache.get(component) ?? null;
  }

  getPayload(component: PortalComponentKey): Record<string, unknown> | null {
    const payload = this.cache.get(component)?.payload;
    return payload && typeof payload === 'object' ? payload : null;
  }

  /**
   * Push a freshly saved admin payload into the session cache so `watchComponent`
   * subscribers update before resolve-batch round-trips.
   */
  patchComponentPayload(
    component: PortalComponentKey,
    payload: Record<string, unknown>,
    layoutDisplayId?: string | null,
  ): void {
    const previous = this.cache.get(component);
    this.cache.set(component, {
      layout_display_id: layoutDisplayId ?? previous?.layout_display_id ?? '',
      component_key: previous?.component_key ?? component,
      layout_name: previous?.layout_name ?? '',
      resolved_via: previous?.resolved_via ?? 'GENERAL',
      payload,
    });
    this.loadedSubject.next(true);
  }

  /** GET resolve for one component — refreshes cache entry used by customer pages. */
  refreshComponentLayout(
    component: PortalComponentKey,
    layoutDisplayId?: string | null,
  ): Observable<ResolvedPortalLayout | null> {
    const layout_display_id = layoutDisplayId?.trim() || undefined;
    return this.resolveOne({ component, layout_display_id }).pipe(
      catchError((err) => {
        console.warn(`[PortalLayoutResolve] refresh ${component} failed`, err);
        return of(null);
      }),
    );
  }

  /**
   * Emits the current cached entry, then re-emits when the portal cache updates
   * (e.g. resolve-batch after login). Used by product-details, orders, shipments, invoices, etc.
   *
   * Session watchers never trigger resolve-batch while logged out; they wait for the login
   * bootstrap. Homepage has no business-partner scope, so every resolve includes the
   * visitor country from the public IP (same lookup as the public desktop homepage).
   *
   * @param useVisitorCountry When true, resolve immediately using the IP-detected country
   *                          even when the visitor is not logged in.
   */
  watchComponent(
    component: PortalComponentKey,
    options?: { useVisitorCountry?: boolean },
  ): Observable<ResolvedPortalLayout | null> {
    const useVisitorCountry = !!options?.useVisitorCountry;
    const readEntry = (): ResolvedPortalLayout | null => this.getEntry(component);

    const sessionReady$ = useVisitorCountry
      ? this.loadForSession([...PORTAL_COMPONENT_KEYS], undefined).pipe(map(() => readEntry()))
      : this.sessionAuthenticatedSubject.pipe(
          switchMap((isAuthenticated) =>
            isAuthenticated
              ? this.loadForSession([...PORTAL_COMPONENT_KEYS], undefined).pipe(map(() => readEntry()))
              : of(readEntry()),
          ),
        );

    const cacheUpdates$ = this.loaded$.pipe(
      filter((loaded) => loaded),
      map(() => readEntry()),
    );

    return merge(sessionReady$, cacheUpdates$).pipe(
      distinctUntilChanged((prev, next) => this.sameResolvedEntry(prev, next)),
    );
  }

  private sameResolvedEntry(
    prev: ResolvedPortalLayout | null,
    next: ResolvedPortalLayout | null,
  ): boolean {
    if (prev === next) {
      return true;
    }
    if (!prev || !next) {
      return prev === next;
    }
    return (
      prev.layout_display_id === next.layout_display_id &&
      prev.resolved_via === next.resolved_via &&
      JSON.stringify(prev.payload) === JSON.stringify(next.payload)
    );
  }

  private applyBatchResponse(data: ResolveBatchResponse | null | undefined): void {
    this.cache.clear();
    if (!data || typeof data !== 'object') {
      this.loadedSubject.next(true);
      return;
    }
    for (const [key, entry] of Object.entries(data)) {
      if (isPortalComponentKey(key) && entry) {
        this.cache.set(key, this.normalizeResolvedEntry(entry, key));
      }
    }
    this.loadedSubject.next(true);
  }

  private resolveVisitorCountry(
    countryCode?: string | null,
  ): Observable<string | null> {
    if (countryCode !== undefined) {
      return of(countryCode);
    }
    return this.getVisitorCountryCode();
  }

  private snapshotBatch(): ResolveBatchResponse {
    const out: ResolveBatchResponse = {};
    for (const key of PORTAL_COMPONENT_KEYS) {
      const entry = this.cache.get(key);
      if (entry) {
        out[key] = entry;
      }
    }
    return out;
  }

  private normalizeResolvedEntry(
    entry: ResolvedPortalLayout,
    component: PortalComponentKey,
  ): ResolvedPortalLayout {
    const payload =
      entry.payload && typeof entry.payload === 'object' && !Array.isArray(entry.payload)
        ? entry.payload
        : {};
    return {
      ...entry,
      component_key: entry.component_key || component,
      layout_display_id: readLayoutDisplayId(entry),
      payload,
    };
  }
}
